import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { preserveOffsetOnSource } from "@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { type RefObject, useEffect, useState } from "react";

export const ALTERNATE_DRAG_TYPE = "alternate";

type AlternateTabDragState =
  | { type: "idle" }
  | { type: "preview"; container: HTMLElement }
  | { type: "dragging" };

interface AlternateTabDragAndDropInput {
  tabRef: RefObject<HTMLDivElement | null>;
  alternateId: string;
  index: number;
  dndInstanceId: string;
  canDrag: boolean;
}

// Makes a tab draggable and a drop target. The list draws the drop indicator.
export function useAlternateTabDragAndDrop(
  input: AlternateTabDragAndDropInput,
): AlternateTabDragState {
  const [dragState, setDragState] = useState<AlternateTabDragState>({
    type: "idle",
  });
  const { tabRef, alternateId, index, dndInstanceId, canDrag } = input;

  useEffect(() => {
    const element = tabRef.current;

    if (!element) {
      return;
    }

    function getDragData() {
      return {
        type: ALTERNATE_DRAG_TYPE,
        alternateId,
        index,
        instanceId: dndInstanceId,
      };
    }

    return combine(
      draggable({
        element,
        canDrag: () => {
          return canDrag;
        },
        getInitialData: getDragData,
        // The native preview would also capture the tab list behind the tab.
        onGenerateDragPreview: ({ location, nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({
              element,
              input: location.current.input,
            }),
            render: ({ container }) => {
              setDragState({ type: "preview", container });
              return () => {
                setDragState({ type: "dragging" });
              };
            },
          });
        },
        onDragStart: () => {
          setDragState({ type: "dragging" });
        },
        onDrop: () => {
          setDragState({ type: "idle" });
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          return (
            source.data.type === ALTERNATE_DRAG_TYPE &&
            source.data.instanceId === dndInstanceId
          );
        },
        getData: ({ input: pointer }) => {
          return attachClosestEdge(getDragData(), {
            input: pointer,
            element,
            allowedEdges: ["left", "right"],
          });
        },
      }),
    );
  }, [tabRef, alternateId, index, dndInstanceId, canDrag]);

  return dragState;
}
